import { lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { NotFound } from "@pages/NotFound";
import { appConfig } from "@shared/utils/appConfig";
import { Helmet } from "react-helmet-async";
import { chatRoutes } from "./chat.routes";
import { contactsRoutes } from "./contacts.routes";
import { authRoutes } from "./auth.routes";
import { dashboardRoutes } from "./dashboard.routes";
import { adminRoutes } from "./admin.routes";
import { registrationRoutes } from "./registration.routes";
import { presentationRoutes } from "./presentation.routes";
import { radarRoutes } from "./radar.routes";
import { RouteGuard } from "./components/RouteGuard";
import { useAppName } from "@shared/hooks/useAppName";
import { useAppDescription } from "@shared/hooks/useAppDescription";
import { useDomainGroup } from "@shared/hooks/useDomainGroup";
import { DomainGroup, domainGroupEnum } from "@shared/utils/domain-group";

const FAVICON_BY_DOMAIN_GROUP: Record<DomainGroup, string> = {
  [domainGroupEnum.SIDEKICK]: "/sidekick-ico.png",
  [domainGroupEnum.RADAR]: "/radar-ico.png",
};

const DevTools = lazy(() =>
  import("@features/DevTools").then((module) => ({
    default: module.DevTools,
  })),
);

const allRoutes = [
  ...chatRoutes,
  ...contactsRoutes,
  ...authRoutes,
  ...dashboardRoutes,
  ...adminRoutes,
  ...registrationRoutes,
  ...presentationRoutes,
  ...radarRoutes,
];

export const AppRoutes = () => {
  const appName = useAppName();
  const appDescription = useAppDescription("long");
  const domainGroup = useDomainGroup();

  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>(
      'link[rel="shortcut icon"]',
    );
    if (favicon) favicon.href = FAVICON_BY_DOMAIN_GROUP[domainGroup];
  }, [domainGroup]);

  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <Helmet>
        <title>{appName}</title>
        <meta name="description" content={appDescription} />
      </Helmet>
      <Routes>
        {allRoutes.map((route) => {
          if (route.enabled === false) return null;
          const {
            enabled: _enabled,
            protected: isProtected,
            roles,
            guestOnly,
            fallback,
            requiredModule,
            ...routeProps
          } = route;
          return (
            <Route
              key={routeProps.path}
              {...routeProps}
              element={
                <RouteGuard
                  protected={isProtected}
                  roles={roles}
                  guestOnly={guestOnly}
                  fallback={fallback}
                  requiredModule={requiredModule}
                >
                  {routeProps.element}
                </RouteGuard>
              }
            />
          );
        })}
        {/* 404 */}
        <Route path={"*"} element={<NotFound />} />
      </Routes>
      {appConfig.SIDEKICK_DEV_TOOLS_ENABLED && <DevTools />}
    </BrowserRouter>
  );
};
